import './Home.css';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { INSTRUCTOR_URL } from './Services/Services';
import 'font-awesome/css/font-awesome.min.css';
import FAQ from './Components/Instructions/FAQ';
import ContactForm from './Components/Footer/ContactForm';
import ClipLoader from 'react-spinners/ClipLoader';
import { Helmet } from 'react-helmet';

const Home = () => {
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();


  useEffect(() => {
    if (userId) {
      setLoading(true);

      const fetchApprovalStatus = async () => {
        try {
          const response = await fetch(`${INSTRUCTOR_URL}/approval-status/${userId}`);
          const data = await response.json();
          setApprovalStatus(data);
        } catch (error) {
          console.error('Error fetching approval status:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchApprovalStatus();
    }
  }, [userId]);

  const handleNavigateDemo = () => {
    if (userId) {
      navigate('/upload-demo')
    }
    else {
      navigate('/login')
    }

  }

  if (loading) {
    return <div className='loading-container'><div className="loading-overlay">
      <ClipLoader color="black" loading={loading} size={30} />
    </div></div>;
  }

  return (
    <>

     <Helmet>
      <title>Instructor Portal | SkillzVerse</title>

      <meta
        name="description"
        content="Access the SkillzVerse Instructor Portal to create, manage, and publish courses, track student progress, and deliver engaging online learning experiences."
      />

      <meta
        name="keywords"
        content="Instructor Portal,Online Teaching Platform,Course Instructor Dashboard,Teach Online,Course Management System"
        />

      <link
        rel="canonical"
        href="https://instructor.skillzverse.com/"
      />
    </Helmet>
    <div>
      <div className="home-container">
        <Header />
        <div className="content-section">
          {approvalStatus && approvalStatus[0] === "APPROVED" ? (
            <ApprovedInstructor navigate={navigate} />
          ) : (
            <DemoRequired navigate={navigate} handleNavigateDemo={handleNavigateDemo} />
          )}
          <CourseCreationTips />
        </div>
      </div>

      <ContactForm />
    </div>
    </>
  );
};


const Header = () => (
  <div className='Home-header'>
    <h1>SkillzVerse Instructor Portal for Course Management</h1>
    <p>
      Our platform empowers you to share your knowledge and reach a global audience.
      Whether you're a seasoned educator or a passionate beginner, we provide the tools
      to help you create, manage, and market your courses effectively.
    </p>
  </div>
);
const ApprovedInstructor = ({ navigate }) => (
  <div className="approval-container">
    <h2>Your Instructor Account is Ready!</h2>
    <div className='demo-video-info-container'>
      <p>
        Congratulations on becoming a SkillzVerse instructor! Follow these steps to create and share your course with students worldwide.
      </p>

      <video
        controls
        autoPlay
        src="https://poc14.s3-accelerate.amazonaws.com/AI-interview-recordings/INSTRUCTIONS/20250315_084945_video.mp4"
        type="video/mp4"
        className="demo-video-component"
        muted={false}
      />
    </div>
    <div className="steps">
      <Step
        icon="fa-pencil-square-o"
        title="Step 1: Craft a Catchy Course Title"
        description="Choose a title that highlights the value of your course. Ensure it is concise, relevant, and enticing."
      />
      <Step
        icon="fa-image"
        title="Step 2: Design a Professional Thumbnail"
        description="A visually appealing thumbnail can help grab attention. Use tools like Canva to create one that stands out."
      />
      <Step
        icon="fa-video-camera"
        title="Step 3: Record High-Quality Videos"
        description="Use a smartphone or camera with good resolution. Ensure proper lighting and clear visuals."
      />
      <Step
        icon="fa-microphone"
        title="Step 4: Enhance Audio Quality"
        description="Invest in a basic external microphone for better sound quality. Avoid background noise during recording."
      />
      <Step
        icon="fa-upload"
        title="Step 5: Upload Your Course"
        description="Once your course materials are ready, upload all content, including videos, quizzes, and resources."
      />
      <Step
        icon="fa-hourglass"
        title="Step 6: Await Review"
        description="Our team will review your course within 48-72 hours. We ensure all content meets our quality guidelines."
      />
      <Step
        icon="fa-check-circle"
        title="Step 7: Course Approved!"
        description="After approval, your course will be live. You can start reaching students and earning revenue immediately."
      />
      <Step
        icon="fa-share-alt"
        title="Step 8: Market Your Course"
        description="Promote your course through social media platforms, email newsletters, and online communities. Engage with potential learners."
      />
    </div>
    <button className="cta-home-button" onClick={() => navigate('/instructor')}>
      Start Creating Your Course
    </button>
    <p className='danger-link' onClick={() => navigate('/course-upload-guidelines')}>course upload guidelines</p>
  </div>
);

const DemoRequired = ({ navigate, handleNavigateDemo }) => (
  <div className="demo-container">
    <h2>Submit Your Demo Video</h2>
    <p  >
      To get started, submit a demo video showcasing your teaching style and skills.
      This ensures our learners get the best experience. Follow these steps:

    </p>


    <div className="steps">
      <Step
        icon="fa-video-camera"
        title="Step 1: Record Your Demo"
        description="Keep it 2-5 minutes long. Focus on explaining a concept clearly."
      />
      <Step
        icon="fa-upload"
        title="Step 2: Upload the Demo Video"
        description="Submit your video through our secure portal for review."
      />
      <Step
        icon="fa-hourglass"
        title="Step 3: Await Approval"
        description="Our team will review your video and notify you shortly."
      />
    </div>
    <button className="cta-home-button" onClick={handleNavigateDemo}>
      Submit Demo Video
    </button>
  </div>
);
const CourseCreationTips = () => (
  <div className="course-tips">
    <h2>Tips for Successful Course Creation</h2>
    <div className="tips-container">
      <div className="tip">
        <div className="icon">
          <i className="fa fa-video-camera"></i>
        </div>
        <h3>Ensure Video Quality</h3>
        <p>
          Use a smartphone or DSLR camera with at least 1080p resolution. Record in a quiet, well-lit environment.
        </p>
      </div>
      <div className="tip">
        <div className="icon">
          <i className="fa fa-pencil"></i>
        </div>
        <h3>Structure Your Course</h3>
        <p>
          Plan your lessons and create a clear, logical flow. Include quizzes and assignments for better engagement.
        </p>
      </div>
      <div className="tip">
        <div className="icon">
          <i className="fa fa-share-alt"></i>
        </div>
        <h3>Promote Your Course</h3>
        <p>
          Share your course on social media, blogs, and forums. Use targeted ads to reach more students.
        </p>
      </div>

      <hr />
      <FAQ />
    </div>
  </div>
);

const Step = ({ icon, title, description }) => (
  <div className="step">
    <div className="icon">
      <i className={`fa ${icon}`}></i>
    </div>
    <h3>{title}</h3>
    <p>{description}</p>
  </div>
  
);

export default Home;
